import type { SessionProfile } from "../../shared/types/domain";
import { demoOrganizationRepository } from "../organization/demoOrganizationRepository";
import { demoPackageLedgerAdapter } from "../sales/demoSalesRepository";
import { demoNotificationAdapter } from "../governance/demoGovernanceRepository";
import type {
  CreateScheduleInput,
  LessonConsumption,
  SchedulingState,
} from "./scheduling.types";

const STORAGE_KEY = "boom-v4-scheduling";
const storeIds = [
  "20000000-0000-4000-8000-000000000001",
  "20000000-0000-4000-8000-000000000002",
  "20000000-0000-4000-8000-000000000003",
  "20000000-0000-4000-8000-000000000004",
];
const seed: SchedulingState = {
  venues: storeIds.flatMap((storeId, index) => [
    {
      id: `40000000-0000-4000-8000-0000000000${index * 2 + 1}`,
      storeId,
      type: "court" as const,
      name: "网球场A",
      active: true,
    },
    {
      id: `40000000-0000-4000-8000-0000000000${index * 2 + 2}`,
      storeId,
      type: "workstation" as const,
      name: "训练工位1",
      active: true,
    },
  ]),
  schedules: [],
  consumptions: [],
  ledger: [],
  reversals: [],
  audit: [],
};
function clone<T>(value: T): T {
  return structuredClone(value);
}
function load(): SchedulingState {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return clone(seed);
  try {
    return JSON.parse(raw) as SchedulingState;
  } catch {
    return clone(seed);
  }
}
function save(state: SchedulingState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}
function fail(message: string): never {
  throw new Error(message);
}
function overlaps(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) {
  return aStart < bEnd && aEnd > bStart;
}
function audit(
  state: SchedulingState,
  actor: SessionProfile,
  action: string,
  entityId: string,
  beforeStatus: string | null,
  afterStatus: string,
  reason: string,
) {
  state.audit.unshift({
    id: crypto.randomUUID(),
    actorId: actor.id,
    action,
    entityId,
    beforeStatus,
    afterStatus,
    reason,
    occurredAt: new Date().toISOString(),
  });
}
async function context(actor: SessionProfile) {
  const org = await demoOrganizationRepository.getState();
  const account = org.accounts.find((item) => item.id === actor.id);
  if (!account || account.status !== "active") fail("当前账号不可用。");
  return org;
}
function canStore(actor: SessionProfile, storeId: string) {
  return actor.role === "super" || actor.storeId === storeId;
}
async function reverseConsumption(
  state: SchedulingState,
  actor: SessionProfile,
  consumption: LessonConsumption,
  reason: string,
) {
  const schedule =
    state.schedules.find((item) => item.id === consumption.scheduleId) ??
    fail("排课不存在。");
  const pkg = await demoPackageLedgerAdapter.reverse(
    consumption.packageId,
    consumption.units,
  );
  const before = schedule.status;
  consumption.status = "reversed";
  schedule.status = "reversed";
  state.ledger.push({
    id: crypto.randomUUID(),
    packageId: consumption.packageId,
    changeUnits: consumption.units,
    balanceAfter: pkg.totalUnits - pkg.usedUnits,
    sourceType: "reversal",
    sourceId: consumption.id,
    occurredAt: new Date().toISOString(),
  });
  audit(
    state,
    actor,
    "consumption.reverse",
    schedule.id,
    before,
    "reversed",
    reason,
  );
  return schedule;
}

export const demoSchedulingRepository = {
  async getState() {
    return load();
  },
  async reset() {
    localStorage.removeItem(STORAGE_KEY);
    return clone(seed);
  },
  async createVenue(
    actor: SessionProfile,
    input: { name: string; type: "court" | "workstation"; storeId: string },
  ) {
    await context(actor);
    if (
      !["manager", "super"].includes(actor.role) ||
      !canStore(actor, input.storeId)
    )
      fail("当前角色不能管理该门店场地。");
    if (!input.name.trim()) fail("场地名称不能为空。");
    const state = load();
    if (
      state.venues.some(
        (v) => v.storeId === input.storeId && v.name === input.name.trim(),
      )
    )
      fail("本店已有同名场地或工位。");
    const venue = {
      id: crypto.randomUUID(),
      storeId: input.storeId,
      type: input.type,
      name: input.name.trim(),
      active: true,
    };
    state.venues.push(venue);
    audit(state, actor, "venue.create", venue.id, null, "active", venue.name);
    save(state);
    return clone(venue);
  },
  async deleteVenue(actor: SessionProfile, venueId: string) {
    await context(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("当前角色不能删除场地。");
    const state = load();
    const index = state.venues.findIndex((venue) => venue.id === venueId);
    if (index < 0) fail("场地不存在或已被删除。");
    const venue = state.venues[index];
    if (!canStore(actor, venue.storeId)) fail("店长只能删除本店场地。");

    const hasHistory = state.schedules.some(
      (schedule) => schedule.venueId === venueId,
    );
    if (hasHistory) {
      venue.active = false;
      audit(
        state,
        actor,
        "venue.archive",
        venue.id,
        "active",
        "inactive",
        `场地“${venue.name}”存在历史排课，已停用并隐藏。`,
      );
    } else {
      state.venues.splice(index, 1);
      audit(
        state,
        actor,
        "venue.delete",
        venue.id,
        "active",
        "deleted",
        `删除未使用场地“${venue.name}”。`,
      );
    }
    save(state);
    return clone(venue);
  },
  async createSchedule(actor: SessionProfile, input: CreateScheduleInput) {
    const org = await context(actor);
    if (!["employee", "manager", "super"].includes(actor.role))
      fail("当前角色不能排课。");
    const member =
      org.accounts.find(
        (a) =>
          a.id === input.memberId &&
          a.role === "member" &&
          a.status === "active",
      ) ?? fail("会员不可用。");
    const coach =
      org.accounts.find(
        (a) =>
          a.id === input.coachId &&
          (a.role === "employee" || a.role === "manager") &&
          a.status === "active",
      ) ?? fail("教练不可用。");
    const state = load();
    const venue =
      state.venues.find((v) => v.id === input.venueId && v.active) ??
      fail("场地不可用。");
    const activeDelegation = org.assignments.some(
      (assignment) =>
        assignment.employeeId === coach.id &&
        assignment.memberId === member.id &&
        assignment.storeId === member.storeId &&
        assignment.status === "active",
    );
    const isServiceCoach =
      member.coachId === coach.id ||
      member.serviceCoachIds?.includes(coach.id) ||
      activeDelegation;
    if (
      !member.storeId ||
      venue.storeId !== member.storeId ||
      !isServiceCoach ||
      (actor.role === "manager" && actor.storeId !== member.storeId) ||
      (actor.role === "employee" && actor.id !== coach.id)
    )
      fail("只有会员已授权的实际授课教练可以排课。");
    const pkg = await demoPackageLedgerAdapter.getPackage(input.packageId);
    if (
      !pkg ||
      pkg.memberId !== member.id ||
      pkg.status !== "active" ||
      pkg.usedUnits >= pkg.totalUnits
    )
      fail("课程包无效或余额不足。");
    const start = new Date(input.startAt),
      end = new Date(input.endAt);
    if (
      !Number.isFinite(start.getTime()) ||
      !Number.isFinite(end.getTime()) ||
      end <= start
    )
      fail("课程时间无效。");
    const minutes = (end.getTime() - start.getTime()) / 60000;
    if (![60, 90, 120].includes(minutes))
      fail("课程时长只能是60、90或120分钟。");
    if (start.toDateString() !== end.toDateString())
      fail("单次课程不可跨越午夜。");
    const active = state.schedules.filter((s) =>
      ["scheduled", "checked_in"].includes(s.status),
    );
    for (const existing of active) {
      if (
        overlaps(
          start,
          end,
          new Date(existing.startAt),
          new Date(existing.endAt),
        )
      ) {
        if (existing.memberId === member.id) fail("会员时间冲突。");
        if (existing.coachId === coach.id) fail("教练时间冲突。");
        if (existing.venueId === venue.id) fail("场地或工位时间冲突。");
      }
    }
    const next = {
      id: crypto.randomUUID(),
      memberId: member.id,
      coachId: coach.id,
      venueId: venue.id,
      packageId: pkg.id,
      storeId: member.storeId,
      startAt: start.toISOString(),
      endAt: end.toISOString(),
      status: "scheduled" as const,
      createdBy: actor.id,
      createdAt: new Date().toISOString(),
    };
    state.schedules.push(next);
    audit(
      state,
      actor,
      "schedule.create",
      next.id,
      null,
      "scheduled",
      "冲突检查通过",
    );
    save(state);
    demoNotificationAdapter.notify({
      recipientId: next.memberId,
      storeId: next.storeId,
      type: "schedule",
      title: "新课程已安排",
      content: `${start.toLocaleString("zh-CN")} 的课程已进入课表。`,
      entityType: "lesson_schedule",
      entityId: next.id,
      dedupeKey: `lesson_schedule:${next.id}:schedule`,
    });
    return clone(next);
  },
  async cancelSchedule(actor: SessionProfile, scheduleId: string) {
    await context(actor);
    const state = load();
    const target =
      state.schedules.find((s) => s.id === scheduleId) ?? fail("排课不存在。");
    if (target.status !== "scheduled") fail("当前状态不能取消。");
    if (actor.role === "member" && target.memberId !== actor.id)
      fail("只能取消本人的课程。");
    if (actor.role === "employee" && target.coachId !== actor.id)
      fail("只有本次实际授课教练可以取消课程。");
    if (actor.role === "manager" && actor.storeId !== target.storeId)
      fail("不能取消其他门店课程。");
    const hours = (new Date(target.startAt).getTime() - Date.now()) / 3600000;
    target.status = hours >= 3 ? "cancelled" : "late_cancelled";
    audit(
      state,
      actor,
      "schedule.cancel",
      target.id,
      "scheduled",
      target.status,
      hours >= 3 ? "提前3小时取消不扣课" : "不足3小时取消，首期不自动扣课",
    );
    save(state);
    return clone(target);
  },
  async checkIn(actor: SessionProfile, scheduleId: string) {
    await context(actor);
    const state = load();
    const target =
      state.schedules.find((s) => s.id === scheduleId) ?? fail("排课不存在。");
    if (target.status !== "scheduled") fail("只有已排课课程可以签到。");
    if (actor.role === "member" && target.memberId !== actor.id)
      fail("只能签到本人课程。");
    if (actor.role === "employee" && target.coachId !== actor.id)
      fail("只有本次实际授课教练可以签到。");
    if (actor.role === "manager" && actor.storeId !== target.storeId)
      fail("不能操作其他门店课程。");
    target.status = "checked_in";
    audit(
      state,
      actor,
      "schedule.check_in",
      target.id,
      "scheduled",
      "checked_in",
      "签到确认",
    );
    save(state);
    return clone(target);
  },
  async consume(
    actor: SessionProfile,
    scheduleId: string,
    idempotencyKey: string,
    units = 1,
  ) {
    await context(actor);
    if (!idempotencyKey.trim()) fail("课消必须携带幂等键。");
    const state = load();
    const target =
      state.schedules.find((s) => s.id === scheduleId) ?? fail("排课不存在。");
    const existing = state.consumptions.find(
      (c) => c.idempotencyKey === idempotencyKey,
    );
    if (existing) {
      if (existing.scheduleId !== target.id) fail("幂等键冲突。");
      return clone(existing);
    }
    if (target.status !== "checked_in") fail("只有已签到课程可以课消。");
    if (actor.role === "employee" && target.coachId !== actor.id)
      fail("只有授课员工可以确认课消。");
    if (
      !["employee", "manager", "super"].includes(actor.role) ||
      (actor.role === "manager" && actor.storeId !== target.storeId)
    )
      fail("当前角色不能确认课消。");
    if (units <= 0 || !Number.isInteger(units * 2))
      fail("扣课课时必须是0.5的正数倍。");
    const pkg = await demoPackageLedgerAdapter.consume(target.packageId, units);
    const consumption = {
      id: crypto.randomUUID(),
      scheduleId: target.id,
      packageId: target.packageId,
      units,
      status: "active" as const,
      operatorId: actor.id,
      completedAt: new Date().toISOString(),
      idempotencyKey,
    };
    target.status = "completed";
    state.consumptions.push(consumption);
    state.ledger.push({
      id: crypto.randomUUID(),
      packageId: target.packageId,
      changeUnits: -units,
      balanceAfter: pkg.totalUnits - pkg.usedUnits,
      sourceType: "consumption",
      sourceId: consumption.id,
      occurredAt: consumption.completedAt,
    });
    audit(
      state,
      actor,
      "consumption.confirm",
      target.id,
      "checked_in",
      "completed",
      `扣减${units}课时`,
    );
    save(state);
    return clone(consumption);
  },
  async requestReversal(
    actor: SessionProfile,
    consumptionId: string,
    reason: string,
    nowOverride?: Date,
  ) {
    await context(actor);
    const state = load();
    const consumption =
      state.consumptions.find((c) => c.id === consumptionId) ??
      fail("课消不存在。");
    if (consumption.status !== "active") fail("课消已经撤销。");
    if (!reason.trim()) fail("撤销原因不能为空。");
    const elapsed =
      ((nowOverride ?? new Date()).getTime() -
        new Date(consumption.completedAt).getTime()) /
      60000;
    if (elapsed <= 10 && actor.id === consumption.operatorId) {
      const schedule = await reverseConsumption(
        state,
        actor,
        consumption,
        reason,
      );
      save(state);
      return { mode: "direct" as const, schedule };
    }
    const schedule =
      state.schedules.find((s) => s.id === consumption.scheduleId) ??
      fail("排课不存在。");
    if (actor.id !== consumption.operatorId)
      fail("只有原课消员工可以申请撤销。");
    schedule.status = "reversal_pending";
    const reversal = {
      id: crypto.randomUUID(),
      consumptionId: consumption.id,
      scheduleId: schedule.id,
      requestedBy: actor.id,
      reason: reason.trim(),
      status: "pending" as const,
      approverId: null,
      createdAt: new Date().toISOString(),
      reviewedAt: null,
    };
    state.reversals.push(reversal);
    audit(
      state,
      actor,
      "reversal.request",
      schedule.id,
      "completed",
      "reversal_pending",
      reason,
    );
    save(state);
    return { mode: "pending" as const, reversal: clone(reversal) };
  },
  async reviewReversal(
    actor: SessionProfile,
    reversalId: string,
    decision: "approve" | "reject",
    reason: string,
  ) {
    await context(actor);
    const state = load();
    const reversal =
      state.reversals.find((r) => r.id === reversalId) ??
      fail("撤销申请不存在。");
    if (reversal.status !== "pending") fail("撤销申请已经处理。");
    const schedule =
      state.schedules.find((s) => s.id === reversal.scheduleId) ??
      fail("排课不存在。");
    if (
      !["manager", "super"].includes(actor.role) ||
      !canStore(actor, schedule.storeId)
    )
      fail("当前角色不能审批该撤销。");
    if (actor.id === reversal.requestedBy) fail("申请人不能自审。");
    reversal.approverId = actor.id;
    reversal.reviewedAt = new Date().toISOString();
    if (decision === "approve") {
      const consumption =
        state.consumptions.find((c) => c.id === reversal.consumptionId) ??
        fail("课消不存在。");
      await reverseConsumption(
        state,
        actor,
        consumption,
        reason || reversal.reason,
      );
      reversal.status = "approved";
    } else {
      if (!reason.trim()) fail("驳回原因不能为空。");
      reversal.status = "rejected";
      const before = schedule.status;
      schedule.status = "completed";
      audit(
        state,
        actor,
        "reversal.reject",
        schedule.id,
        before,
        "completed",
        reason,
      );
    }
    save(state);
    return clone(reversal);
  },
};
